import { ShipTier } from "@voidex/contracts-ts";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useState } from "react";
import {
	formatZoneDuration,
	formatZoneMultiplier,
	getZoneDuration,
	getZoneColor,
} from "../../services/zoneUtils";
import { useSpaceStore } from "../../stores";
import { FarmingWizard } from "./FarmingWizard";
import { MissionAnimation } from "./MissionAnimation";

type ModalPhase = "wizard" | "animating";

export function MissionLaunchModal() {
	const farmingTarget = useSpaceStore((s) => s.farmingTarget);
	const closeMission = useSpaceStore((s) => s.closeMission);
	const setActiveTab = useSpaceStore((s) => s.setActiveTab);

	const [phase, setPhase] = useState<ModalPhase>("wizard");
	const [launchedTier, setLaunchedTier] = useState<ShipTier>(ShipTier.Probe);

	// Called by FarmingWizard when mission tx confirms
	const handleLaunched = useCallback((shipTier: ShipTier) => {
		setLaunchedTier(shipTier);
		setPhase("animating");
	}, []);

	// Reset everything when modal closes
	const handleClose = useCallback(() => {
		closeMission();
		setPhase("wizard");
	}, [closeMission]);

	// Close modal and navigate to missions tab
	const handleViewMission = useCallback(() => {
		closeMission();
		setPhase("wizard");
		setActiveTab("missions");
	}, [closeMission, setActiveTab]);

	// Calculate expected farm duration based on zone
	const expectedDuration = farmingTarget ? getZoneDuration(farmingTarget.system.zone) : 0;

	function handleBackdropClick(e: React.MouseEvent) {
		if (e.target === e.currentTarget) {
			handleClose();
		}
	}

	return (
		<AnimatePresence>
			{farmingTarget && (
				<motion.div
					className="mission-modal__backdrop"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.2 }}
					onClick={handleBackdropClick}
				>
					<motion.div
						className={`mission-modal glass-panel-elevated ${phase !== "wizard" ? "mission-modal--cinematic" : ""}`}
						initial={{ opacity: 0, scale: 0.95, y: 20 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.95, y: 20 }}
						transition={{ type: "spring", damping: 25, stiffness: 300 }}
					>
						{/* Header — visible in wizard phase only */}
						{phase === "wizard" && (
							<div className="mission-modal__header">
								<div className="mission-modal__planet">
									<span
										className="mission-modal__planet-dot"
										style={{ background: farmingTarget.planet.color }}
									/>
									<div className="mission-modal__planet-info">
										<h2 className="mission-modal__title">Mission to {farmingTarget.planet.name}</h2>
										<span className="mission-modal__subtitle">
											{farmingTarget.system.star.name} System
											<span
												className="mission-modal__zone-tag"
												style={{ background: getZoneColor(farmingTarget.system.zone) }}
											>
												Zone {farmingTarget.system.zone}
											</span>
											<span className="mission-modal__zone-meta">
												{formatZoneDuration(farmingTarget.system.zone)} &middot;{" "}
												{formatZoneMultiplier(farmingTarget.system.zone)}
											</span>
										</span>
									</div>
								</div>
								<button
									type="button"
									className="mission-modal__close"
									onClick={handleClose}
									aria-label="Close mission modal"
								>
									<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
										<title>Close</title>
										<path
											d="M15 5L5 15M5 5l10 10"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
										/>
									</svg>
								</button>
							</div>
						)}

						<div className="mission-modal__body">
							{phase === "wizard" && <FarmingWizard onLaunched={handleLaunched} />}

							{phase === "animating" && (
								<MissionAnimation
									shipTier={launchedTier}
									planetName={farmingTarget.planet.name}
									planetColor={farmingTarget.planet.color}
									systemName={farmingTarget.system.star.name}
									expectedDuration={expectedDuration}
									onAffirmative={handleClose}
									onViewMission={handleViewMission}
								/>
							)}
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
